<template>
  <div v-if="showSummary" class="flex-grow-0 pa-2 bg-white border-top">
    <div class="caption font-weight-bold grey--text text--darken-3 mb-1 px-1 d-flex align-center justify-space-between">
      <span>
        <v-icon x-small color="primary" class="mr-1">mdi-chart-bell-curve-cumulative</v-icon>
        CONSOLIDADO DE PROGRAMACIÓN COMERCIAL Y SALDOS VS. PRESUPUESTO MAESTRO
      </span>
      <span class="caption grey--text">
        {{ activeLinesCount }} líneas comerciales registradas
      </span>
    </div>

    <v-row dense>
      <!-- Card 1: Total TM Programadas -->
      <v-col cols="12" sm="3" md="auto" style="flex: 1;">
        <div class="kpi-card pa-2 rounded-lg border-light bg-white">
          <div class="kpi-label">TOTAL TM PROG.</div>
          <div class="d-flex align-baseline mt-1">
            <span class="kpi-value text-subtitle-1 font-weight-black primary--text">
              {{ formatNumber(summary.ProgrammedTons, 2) }}
            </span>
            <span class="kpi-unit ml-1 caption font-weight-bold grey--text">TM</span>
          </div>
          <div class="caption grey--text mt-1 text-truncate">
            Ppto: {{ formatNumber(budgetBaseline.TotalTons, 2) }} TM
          </div>
        </div>
      </v-col>

      <!-- Card 2: Saldo TM -->
      <v-col cols="12" sm="3" md="auto" style="flex: 1;">
        <div class="kpi-card pa-2 rounded-lg border-light" :class="summary.BalanceTons < 0 ? 'red lighten-5' : 'bg-white'">
          <div class="kpi-label" :class="summary.BalanceTons < 0 ? 'red--text text--darken-3' : ''">SALDO TM</div>
          <div class="d-flex align-baseline mt-1">
            <span class="kpi-value text-subtitle-1 font-weight-black" :class="summary.BalanceTons < 0 ? 'red--text font-weight-black' : 'green--text text--darken-3'">
              {{ formatNumber(summary.BalanceTons, 2) }}
            </span>
            <span class="kpi-unit ml-1 caption font-weight-bold" :class="summary.BalanceTons < 0 ? 'red--text' : 'grey--text'">TM</span>
          </div>
          <div class="caption mt-1 font-weight-medium" :class="summary.BalanceTons < 0 ? 'red--text text--darken-4' : 'grey--text'">
            {{ summary.BalanceTons < 0 ? '⚠️ Sobre Ppto' : 'Disponible' }}
          </div>
        </div>
      </v-col>

      <!-- Card 3: Total FCL Programados -->
      <v-col cols="12" sm="3" md="auto" style="flex: 1;">
        <div class="kpi-card pa-2 rounded-lg border-light bg-white">
          <div class="kpi-label">TOTAL FCL PROG.</div>
          <div class="d-flex align-baseline mt-1">
            <span class="kpi-value text-subtitle-1 font-weight-black grey--text text--darken-4">
              {{ formatNumber(summary.ProgrammedFcl, 1) }}
            </span>
            <span class="kpi-unit ml-1 caption font-weight-bold grey--text">FCL</span>
          </div>
          <div class="caption grey--text mt-1 text-truncate">
            Ppto: {{ formatNumber(budgetBaseline.TotalFcl, 1) }} FCL
          </div>
        </div>
      </v-col>

      <!-- Card 4: Saldo FCL -->
      <v-col cols="12" sm="3" md="auto" style="flex: 1;">
        <div class="kpi-card pa-2 rounded-lg border-light" :class="summary.BalanceFcl < 0 ? 'red lighten-5' : 'bg-white'">
          <div class="kpi-label" :class="summary.BalanceFcl < 0 ? 'red--text text--darken-3' : ''">SALDO FCL</div>
          <div class="d-flex align-baseline mt-1">
            <span class="kpi-value text-subtitle-1 font-weight-black" :class="summary.BalanceFcl < 0 ? 'red--text' : 'grey--text text--darken-4'">
              {{ formatNumber(summary.BalanceFcl, 1) }}
            </span>
            <span class="kpi-unit ml-1 caption font-weight-bold" :class="summary.BalanceFcl < 0 ? 'red--text' : 'grey--text'">FCL</span>
          </div>
          <div class="caption mt-1 font-weight-medium" :class="summary.BalanceFcl < 0 ? 'red--text text--darken-4' : 'grey--text'">
            {{ summary.BalanceFcl < 0 ? '⚠️ Sobre Ppto' : 'Disponible' }}
          </div>
        </div>
      </v-col>

      <!-- Card 5: Total Contractual -->
      <v-col cols="12" sm="3" md="auto" style="flex: 1;">
        <div class="kpi-card pa-2 rounded-lg border-light bg-white">
          <div class="kpi-label">TOTAL CONTRACTUAL</div>
          <div class="d-flex align-baseline mt-1">
            <span class="kpi-value text-subtitle-1 font-weight-black grey--text text--darken-4">
              ${{ formatNumber(summary.ProgrammedContractUsd, 2) }}
            </span>
            <span class="kpi-unit ml-1 caption font-weight-bold grey--text">USD</span>
          </div>
          <div class="caption grey--text mt-1 text-truncate">
            Importe pactado
          </div>
        </div>
      </v-col>

      <!-- Card 6: Total FOB Equivalente -->
      <v-col cols="12" sm="3" md="auto" style="flex: 1;">
        <div class="kpi-card pa-2 rounded-lg border-light bg-white">
          <div class="kpi-label">TOTAL FOB EQ PROG.</div>
          <div class="d-flex align-baseline mt-1">
            <span class="kpi-value text-subtitle-1 font-weight-black green--text text--darken-3">
              ${{ formatNumber(summary.ProgrammedFobUsd, 2) }}
            </span>
            <span class="kpi-unit ml-1 caption font-weight-bold grey--text">USD</span>
          </div>
          <div class="caption grey--text mt-1 text-truncate">
            Ppto: ${{ formatNumber(budgetBaseline.TotalFobUsd, 2) }}
          </div>
        </div>
      </v-col>

      <!-- Card 7: Saldo FOB -->
      <v-col cols="12" sm="3" md="auto" style="flex: 1;">
        <div class="kpi-card pa-2 rounded-lg border-light" :class="summary.BalanceFobUsd < 0 ? 'red lighten-5' : 'bg-white'">
          <div class="kpi-label" :class="summary.BalanceFobUsd < 0 ? 'red--text text--darken-3' : ''">SALDO FOB</div>
          <div class="d-flex align-baseline mt-1">
            <span class="kpi-value text-subtitle-1 font-weight-black" :class="summary.BalanceFobUsd < 0 ? 'red--text' : 'green--text text--darken-3'">
              ${{ formatNumber(summary.BalanceFobUsd, 2) }}
            </span>
            <span class="kpi-unit ml-1 caption font-weight-bold" :class="summary.BalanceFobUsd < 0 ? 'red--text' : 'grey--text'">USD</span>
          </div>
          <div class="caption mt-1 font-weight-medium" :class="summary.BalanceFobUsd < 0 ? 'red--text text--darken-4' : 'grey--text'">
            {{ summary.BalanceFobUsd < 0 ? '⚠️ Sobre el presupuesto' : 'Disponible' }}
          </div>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { formatNumber } from "../utils/CommercialProgrammingCalculations";

export default {
  name: "CommercialProgramSummary",
  props: {
    showSummary: {
      type: Boolean,
      default: true,
    },
    summary: {
      type: Object,
      required: true,
    },
    budgetBaseline: {
      type: Object,
      required: true,
    },
    activeLinesCount: {
      type: Number,
      default: 0,
    },
  },
  methods: {
    formatNumber,
  },
};
</script>

<style scoped>
.border-top {
  border-top: 1px solid #e2e8f0 !important;
}
.border-light {
  border: 1px solid #e2e8f0 !important;
}
.kpi-card {
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
  transition: all 0.2s ease;
}
.kpi-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.5px;
  line-height: 1.2;
}
.kpi-value {
  color: #0f172a;
}
.kpi-unit {
  font-size: 11px;
}
</style>
